import { create } from 'zustand';
import { devtools, persist, type StorageValue } from 'zustand/middleware';
import type { SetupFormData } from '@/features/wholesaler-setup/types/setup';
import type { SetupStoreState } from '@/features/wholesaler-setup/types/setup-store-state';

const INITIAL_FORM_DATA: SetupFormData = {
  fullName: '',
  businessEmail: '',
  role: '',
  companyName: '',
  businessType: '',
  countryCode: 'AU',
  regions: [],
  carriagePreferences: [],
  countryCarriage: [],
  services: [],
  enabledFeatures: [],
  isNewAccount: true,
  additionalCredit: 0,
  paymentMethodId: '',
  nameOnCard: '',
  portalHostname: '',
  dnsAcknowledged: false,
  loginBackground: 'Bing Image of the Day',
  logoDark: null,
  logoLight: null,
  favicon: null,
};

export const useSetupStore = create<SetupStoreState>()(
  devtools(
    persist(
      (set) => ({
        currentStep: 'personal-details',
        formData: INITIAL_FORM_DATA,
        flowMode: 'default',
        draftWholesalerName: null,
        completedSections: [],

        setStep: (step) => {
          set({ currentStep: step });
        },

        updateFormData: (data) => {
          set((state) => {
            const newFormData = { ...state.formData, ...data };
            return {
              formData: newFormData,
              draftWholesalerName:
                state.flowMode === 'new-wholesaler' && typeof data.companyName === 'string'
                  ? data.companyName
                  : state.draftWholesalerName,
            };
          });
        },

        markSectionComplete: (sectionIndex) => {
          set((state) => ({
            completedSections: state.completedSections.includes(sectionIndex)
              ? state.completedSections
              : [...state.completedSections, sectionIndex],
          }));
        },

        startNewWholesalerSetup: (companyName, isNewAccount) => {
          set({
            currentStep: 'company',
            formData: { ...INITIAL_FORM_DATA, companyName: companyName ?? '', isNewAccount },
            flowMode: 'new-wholesaler',
            draftWholesalerName: companyName ?? null,
            completedSections: [],
          });
        },

        clearNewWholesalerSetup: () => {
          set({ flowMode: 'default', draftWholesalerName: null });
        },

        reset: () => {
          set({
            currentStep: 'personal-details',
            formData: INITIAL_FORM_DATA,
            flowMode: 'default',
            draftWholesalerName: null,
            completedSections: [],
          });
        },
      }),
      {
        name: 'wasp-setup',
        // Use sessionStorage to clear on browser close (security best practice for form data)
        storage: {
          getItem: (name): StorageValue<SetupStoreState> | null => {
            const str = sessionStorage.getItem(name);
            return str ? (JSON.parse(str) as StorageValue<SetupStoreState>) : null;
          },
          setItem: (name, value) => {
            sessionStorage.setItem(name, JSON.stringify(value));
          },
          removeItem: (name) => {
            sessionStorage.removeItem(name);
          },
        },
        // File objects are not JSON-serializable — exclude them from persistence.
        // They live only in memory and reset on page refresh, which is intentional.
        partialize: (state) => ({
          ...state,
          formData: {
            ...state.formData,
            logoDark: null,
            logoLight: null,
            favicon: null,
          },
        }),
      },
    ),
    { name: 'setup-store' },
  ),
);
